/** La consegna, dal lato della pagina.
 *
 * Si chiama come l'instradamento del servizio e non come la schermata: due
 * file che differiscono solo per la maiuscola si scambiano fra loro su un
 * archivio che ignora le maiuscole, ed e' successo gia' una volta (GI-122).
 */
import { download, get, newKey, send } from './api'
import type { Draft, Item } from './types'

export interface Receipt {
  export_id: string
  state: string
  checksum: string
  catalog_version: string
  issued_at: string
  author: string | null
  warning_note: string | null
  review_epoch: number | null
  frozen_review_version: number | null
  supervisor_review_version: number | null
  quality_seal_id: string | null
  issues: number
  supersedes_id: string | null
  rejection_reason: string | null
  external_reference: string | null
  outcome_at: string | null
  /** Di quale commessa e' la ricevuta (GI-279): per il percorso di ritorno. */
  inspection_id?: string | null
  external_inspection_id?: number | null
}

export function compose(inspectionId: string, key = newKey()): Promise<Draft> {
  return send<Draft>('/exports', { inspection_id: inspectionId }, key)
}

export function show(exportId: string): Promise<Draft> {
  return get<Draft>(`/exports/${exportId}`)
}

/** Che cosa e' entrato nella consegna, riga per riga.
 *
 * A parte dalla consegna e non dentro: su una commessa vera sono centinaia, e
 * chi apre la pagina per sapere se puo' inviare non deve aspettarle. */
export function items(exportId: string): Promise<Item[]> {
  return get<Item[]>(`/exports/${exportId}/items`)
}

export function validate(exportId: string, key = newKey()): Promise<Draft> {
  return send<Draft>(`/exports/${exportId}/validate`, {}, key)
}

/** L'invio. La motivazione scritta accompagna la consegna quando restano
 *  avvisi aperti: gli errori bloccano, gli avvisi no — ma la scelta di inviare
 *  lo stesso resta scritta accanto alla consegna (§ 10). */
export function submit(exportId: string, note: string | null = null, key = newKey()): Promise<Draft> {
  return send<Draft>(`/exports/${exportId}/submit`, { note }, key)
}

export function receipt(exportId: string): Promise<Receipt> {
  return get<Receipt>(`/exports/${exportId}/receipt`)
}

/** L'esito comunicato dal committente. Senza questi due, una consegna resta
 *  «inviata» per sempre e nessuno sa se e' stata accettata. */
export function accepted(exportId: string, key = newKey()): Promise<Draft> {
  return send<Draft>(`/exports/${exportId}/accepted`, {}, key)
}

export function rejected(
  exportId: string,
  reason: string,
  externalReference: string,
  key = newKey(),
): Promise<Draft> {
  return send<Draft>(
    `/exports/${exportId}/rejected`,
    { reason, external_reference: externalReference },
    key,
  )
}

/** Il documento congelato, come e' partito. */
export function document(exportId: string): Promise<unknown> {
  return get<unknown>(`/exports/${exportId}/document`)
}

/** Una consegna partita, vista da fuori della sua commessa. */
export interface Delivered {
  export_id: string
  inspection_id: string
  external_inspection_id: number
  state: string
  sent_at: string
  declarations: number
  sent_by: string | null
  checksum: string
  rejection_reason: string | null
}

/** Le consegne di tutte le commesse, le piu' fresche in cima.
 *
 * Senza filtri e senza pagine: sono unita' l'anno. Il giorno in cui saranno
 * tante, il filtro si aggiungera' sapendo per che cosa. */
export function sent(): Promise<Delivered[]> {
  return get<Delivered[]>('/exports')
}

/** Le due scale affiancate: le nostre soglie e le fasce del § 6.5.
 *
 * Sta accanto alla consegna e non dentro perche' non la cambia: e' la
 * differenza fra i due modelli, mostrata dove costa zero mostrarla, finche' il
 * committente non risponde alla domanda 8.7. */
export interface TwoScales {
  element_external_id: number
  distance_m: number
  ours: string
  theirs: string
  verdict: string
  why: string
}

export interface Scales {
  voltage_kv: number
  conductor: string
  our_touch_m: number
  their_urgent_to_m: number
  our_limit_m: number
  their_table_to_m: number
  contact_gap_m: number
  unattributed_gap_m: number
  agreeing: number
  diverging: number
  incomparable: number
  unexplained: number
  rows: TwoScales[]
}

export function scales(exportId: string): Promise<Scales> {
  return get<Scales>(`/exports/${exportId}/scales`)
}

/** Il rapporto di commessa da leggere e stampare (GI-258): HTML con lo stile
 *  di stampa, preso con le credenziali e aperto in una scheda. */
export function report(inspectionId: string): Promise<Blob> {
  return download(`/inspections/${encodeURIComponent(inspectionId)}/report`)
}
